import {
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import isEqual from 'lodash-es/isEqual';
import moment from 'moment';
import classNames from 'classnames/bind';
import { useInView } from 'react-intersection-observer';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretUp } from '@fortawesome/pro-solid-svg-icons/faCaretUp';
import { faCaretDown } from '@fortawesome/pro-solid-svg-icons/faCaretDown';
import { faRedo } from '@fortawesome/pro-solid-svg-icons/faRedo';
import { faFilter } from '@fortawesome/pro-solid-svg-icons/faFilter';

// services
import InsiderTransactionsSvc from '../../../services/dbServices/InsiderTransactionsSvc';

import withComponentName from '../../../decorators/withComponentName';

import removeDuplicates from '../../../helpers/removeDuplicates';
import convertPermalinkToPath from '../../../helpers/urlHelpers';
import capitalize from '../../../helpers/capitalize';
import { getSavedFilters, setFilter } from '../../../utils/insiderTransactionsFilters';
import { checkAndRoun } from '../../../helpers/topicsHelpers';

import { setWatchlistLoadStatus } from '../../../actions/watchlistActions';

import INSIDER_TRANSACTIONS_TABLE_COLUMNS from '../../../data/directory/insiderTransactions/insiderTransactionsTableColumns';
import { INSIDER_TRANSACTIONS } from '../../../data/directory/topic_classes/tabPaths';
import initialTransactionTypes from '../../../data/directory/insiderTransactions/transactionTypes';
import initialTransactionInfoTypes from '../../../data/directory/insiderTransactions/transactionInfoTypes';
import { INSIDER_TX_VALUE_SORTING } from '../../../data/features';

import useSizes from '../../../hooks/sizes';

import InsiderTransactionsTabOptionsContext from '../Topic/InsiderTransactionsTab/InsiderTransactionsTabOptionsContext';

import Pagination from '../PremiumPublications/Pagination';
import FilterButton from '../../Buttons/FilterButton';
import CFDropdown from '../../Shared/CFDropdown';
import TimePeriodFilter from '../../Shared/TimePeriodFilter';
import Preloader from '../../Preloader';
import CustomButton from '../../Buttons/CustomButton';
import TransparentButton from '../../Buttons/PreferencesButtons/TransparentButton';
import Tooltip from '../../Tooltip';
import DataTableCollapsable from '../../Shared/DataTableCollapsable';
import PremiumBadge from '../../Shared/PremiumBadge';
import CFLink from '../../Shared/CFLink';

import Styles from './styles.module.scss';

const cx = classNames.bind(Styles);

export const InsiderTransactionsTable = ({
  identifier = 'SINGLE_PAGE',
  officers: initialOfficers,
  singlePage,
  multipleCompanies,
  companiesData,
}) => {
  const tabletViewChatbotOpened = useSelector(({ chatbot, common }) => chatbot.isChatbotOpened
  && (common.width > 1024 && common.width < 1500));
  const [transactionsData, setTransactionsData] = useState({
    data: [],
    loading: true,
    sortField: null,
    sortOrder: 'asc',
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(multipleCompanies ? '25' : '15');
  const [filtersOpened, setFiltersOpened] = useState(false);
  // transaction types
  const [transactionTypes, setTransactionTypes] = useState(initialTransactionTypes);
  const [selectedTransactionTypes, setSelectedTransactionTypes] = useState(
    transactionTypes
      .filter((type) => type.selected)
      .map((type) => type.value),
  );
  // transaction info types
  const [transactionInfoTypes, setTransactionInfoTypes] = useState(initialTransactionInfoTypes);
  const [selectedTransactionInfoTypes, setSelectedTransactionInfoTypes] = useState(
    transactionInfoTypes
      .filter((type) => type.selected)
      .map((type) => type.value),
  );
  // officers
  const [officers, setOfficers] = useState(
    initialOfficers.map((officer) => ({
      label: officer.name,
      value: officer.name,
      selected: true,
    })),
  );
  const [selectedOfficers, setSelectedOfficers] = useState(
    initialOfficers.map((officer) => officer.name),
  );
  const [timeRange, setTimeRange] = useState(() => {
    const startDate = new Date();
    startDate.setMonth(startDate.getMonth() - 1);

    return {
      startDate,
      endDate: new Date(),
      dateFilterType: 'lifetime',
    };
  });
  // dummy variable that used to refresh charts data
  const [refreshTimestamp, setRefreshTimestamp] = useState(null);

  const { width } = useSizes();
  const { isWatchlistPage } = useContext(InsiderTransactionsTabOptionsContext);
  const features = useSelector(({ subscriptions }) => (
    subscriptions.permissions.features
  ));
  const sortingPlan = useSelector(({ subscriptions }) => (
    subscriptions.permissions.user.obtainable_levels[INSIDER_TX_VALUE_SORTING]?.[0]
  ));

  const dispatch = useDispatch();
  const wlPageLoadingStep = useSelector((state) => state.watchlistReducer.wlPageLoadingStep);

  const { ref: lastElementRef, inView: lastElementInView } = useInView({
    rootMargin: '150px',
    skip: transactionsData.loading,
  });

  const initialized = useRef(false);

  const toggleFilters = useCallback(() => {
    setFiltersOpened((current) => !current);
  }, []);

  const updateRefreshFlag = useCallback(() => {
    setRefreshTimestamp(Date.now());
  }, []);

  const reset = useCallback(() => {
    setTransactionTypes((current) => (
      current.map((item) => ({
        label: item.label,
        value: item.value,
        selected: true,
      }))
    ));
    setTransactionInfoTypes((current) => (
      current.map((item) => ({
        label: item.label,
        value: item.value,
        selected: true,
      }))
    ));
    setTimeRange({
      startDate: new Date(),
      endDate: new Date(),
      dateFilterType: 'lifetime',
    });
    setOfficers((current) => (
      current.map((item) => ({
        label: item.label,
        value: item.value,
        selected: true,
      }))
    ));
  }, []);

  const onSort = useCallback((field) => {
    setTransactionsData((current) => {
      const { data, sortField } = current;
      let { sortOrder } = current;

      if (sortField === field) {
        sortOrder = sortOrder === 'asc' ? 'desc' : 'asc';
        data.reverse();
      } else {
        sortOrder = 'asc';
        let sortFn;
        switch (field) {
          case 'date':
            sortFn = (a, b) => (
              new Date(a.mainData.transactionDate) - new Date(b.mainData.transactionDate)
            );
            break;
          case 'filingDateFormatted':
            sortFn = (a, b) => new Date(a.mainData.filingDate) - new Date(b.mainData.filingDate);
            break;
          case 'valueFormatted':
            sortFn = (a, b) => a.mainData.value - b.mainData.value;
            break;
          default:
            sortFn = (a, b) => (
              a.mainData[field] < b.mainData[field]
                ? -1
                : Number(a.mainData[field] !== b.mainData[field])
            );
            break;
        }
        data.sort(sortFn);
      }

      return {
        data,
        loading: current.loading,
        sortField: field,
        sortOrder,
      };
    });
  }, []);

  const insiderTransactionsTableColumns = useMemo(() => {
    let fields = INSIDER_TRANSACTIONS_TABLE_COLUMNS.map((initialField) => {
      const field = {
        ...initialField,
      };

      if (multipleCompanies) {
        field.sort = false;
      }

      if (
        field.key === 'valueFormatted'
        && !features.includes(INSIDER_TX_VALUE_SORTING)
        && sortingPlan
        && !multipleCompanies
      ) {
        field.sort = false;
        field.title = (
          <>
            Value
            <Tooltip
              icon={(
                <div className={cx('arrows')}>
                  <FontAwesomeIcon icon={faCaretUp} className={cx('asc')} />
                  <FontAwesomeIcon icon={faCaretDown} className={cx('desc')} />
                </div>
              )}
              className={cx('premium-tooltip')}
              tooltipClassName={cx('premium-tooltip-content')}
              dynamicPosition
              contentStyle={{ left: 0 }}
            >
              <CFLink to="/pricing" external={false}>
                Upgrade
              </CFLink>
              &nbsp;to&nbsp;
              {capitalize(sortingPlan.split('_')[0])}
              &nbsp;to use this feature.
            </Tooltip>
            <PremiumBadge plan={sortingPlan} />
          </>
        );
      }

      if (field.key === 'transactionInfoType') {
        field.title = (
          <>
            Type
            <Tooltip
              className={cx('tooltip')}
              tooltipClassName={cx('tooltip-content')}
              dynamicPosition
              stopPropagations
            >
              Uninformative transactions are those that have been scheduled in advanced
              for a predetermined time and number of shares, based on Rule 10b5-1 of the SEC.
              They are automatically triggered.
            </Tooltip>
          </>
        );
      }

      return field;
    });

    if (multipleCompanies) {
      fields.unshift({
        key: 'companyName',
        title: 'Company',
      });
    }

    if (width < 768) {
      fields = fields.filter((field) => (
        [multipleCompanies ? 'companyName' : 'name', 'transactionType', 'valueFormatted'].includes(field.key)
      ));
    } else if (width <= 1024 || (width <= 1700 && isWatchlistPage) || tabletViewChatbotOpened) {
      fields = fields.filter((field) => (
        [multipleCompanies ? 'companyName' : 'name', 'date', 'transactionType', 'valueFormatted'].includes(field.key)
      ));
    }

    return fields;
  }, [
    width <= 1024,
    width < 768,
    multipleCompanies,
    features,
    sortingPlan,
    isWatchlistPage,
    tabletViewChatbotOpened,
  ]);

  const isDefaultSettings = useMemo(() => (
    transactionTypes.length === selectedTransactionTypes.length
    && transactionInfoTypes.length === selectedTransactionInfoTypes.length
    && officers.length === selectedOfficers.length
    && timeRange.dateFilterType === 'lifetime'
  ), [
    transactionTypes,
    selectedTransactionTypes,
    transactionInfoTypes,
    selectedTransactionInfoTypes,
    officers,
    selectedOfficers,
    timeRange,
  ]);

  useEffect(() => {
    if (lastElementInView) {
      setCurrentPage((currentPage) => currentPage + 1);
    }
  }, [lastElementInView]);

  useEffect(() => {
    if (initialized.current) {
      setFilter(identifier, 'transactionTypes', selectedTransactionTypes);
    }
  }, [identifier, selectedTransactionTypes]);

  useEffect(() => {
    if (initialized.current) {
      setFilter(identifier, 'transactionInfoTypes', selectedTransactionInfoTypes);
    }
  }, [identifier, selectedTransactionInfoTypes]);

  useEffect(() => {
    if (initialized.current) {
      setFilter(identifier, 'officers', selectedOfficers);
    }
  }, [identifier, selectedOfficers]);

  useEffect(() => {
    if (initialized.current) {
      setFilter(identifier, 'timeRange', timeRange);
    }
  }, [identifier, timeRange]);

  useEffect(() => {
    if (multipleCompanies) {
      return;
    }

    if (width > 1024) {
      setPageSize('15');
    } else {
      setPageSize('10');
    }
  }, [width > 1024, multipleCompanies]);

  useEffect(() => {
    setCurrentPage(1);
  }, [
    pageSize,
    selectedTransactionTypes,
    selectedTransactionInfoTypes,
    selectedOfficers,
    timeRange,
    refreshTimestamp,
  ]);

  useEffect(() => {
    setOfficers(
      initialOfficers.map((officer) => ({
        label: officer.name,
        value: officer.name,
        selected: true,
      })),
    );
    setSelectedOfficers(
      initialOfficers.map((officer) => officer.name),
    );
  }, [initialOfficers]);

  const dataFetchingDeps = [
    identifier,
    transactionTypes,
    selectedTransactionTypes,
    transactionInfoTypes,
    selectedTransactionInfoTypes,
    officers,
    selectedOfficers,
    timeRange,
    refreshTimestamp,
    singlePage,
    multipleCompanies,
    companiesData,
    width <= 1024,
    width < 768,
    isWatchlistPage,
  ];

  if (multipleCompanies) {
    dataFetchingDeps.push(
      currentPage,
      pageSize,
    );
  }

  useEffect(() => {
    // For single IT page fetch filters first and fetch transactions after
    if (initialized.current || !multipleCompanies) {
      return;
    }

    (async () => {
      const savedFilters = await getSavedFilters(identifier);

      setTransactionTypes((currentTypes) => (
        currentTypes.map((type) => ({
          ...type,
          selected: (
            savedFilters
              ?.transactionTypes
              ?.includes(type.value)
            ?? true
          ),
        }))
      ));
      setTransactionInfoTypes((currentInfoTypes) => (
        currentInfoTypes.map((type) => ({
          ...type,
          selected: (
            savedFilters
              ?.transactionInfoTypes
              ?.includes(type.value)
            ?? true
          ),
        }))
      ));

      if (savedFilters?.timeRange && !isEqual(savedFilters?.timeRange, timeRange)) {
        setTimeRange(savedFilters.timeRange);
      }

      initialized.current = true;
    })();
  }, [multipleCompanies, timeRange]);

  useEffect(() => {
    let mounted = true;

    if (!identifier && !singlePage) {
      return;
    }

    // For multiple companies IT page fetch filters first and fetch transactions after
    if (multipleCompanies && !initialized.current) {
      return;
    }

    if (
      !multipleCompanies
      && initialized.current
      && (
        (!multipleCompanies && !selectedOfficers.length)
        || !selectedTransactionTypes.length
        || !selectedTransactionInfoTypes.length
      )
    ) {
      setTransactionsData({
        data: [],
        loading: false,
        sortField: null,
        sortOrder: 'asc',
      });
      return;
    }

    const timer = setTimeout(() => {
      setTransactionsData((current) => ({
        data: current.data,
        loading: true,
        sortField: current.sortField,
        sortOrder: current.sortOrder,
      }));

      InsiderTransactionsSvc.getCompanyInsiderTransactions({
        ...(multipleCompanies && {
          per: pageSize,
          page: currentPage,
        }),
        ...(!singlePage && {
          identifier,
        }),
        ...(officers.length !== selectedOfficers.length && {
          officers: selectedOfficers.join(','),
        }),
        ...(transactionTypes.length !== selectedTransactionTypes.length && {
          transactionTypes: selectedTransactionTypes.join(','),
        }),
        ...(transactionInfoTypes.length !== selectedTransactionInfoTypes.length && {
          transactionInfoTypes: selectedTransactionInfoTypes.join(','),
        }),
        startDate: timeRange.dateFilterType === 'lifetime' ? null : new Date(timeRange.startDate),
        endDate: timeRange.dateFilterType === 'lifetime' ? null : new Date(timeRange.endDate),
        refreshTimestamp,
      }).then(async (transactions) => {
        if (!mounted) {
          return;
        }

        if (Array.isArray(transactions)) {
          if (!multipleCompanies) {
            transactions.sort((a, b) => (
              new Date(b.transactionDate) - new Date(a.transactionDate)
            ));
          }

          const contentDataMapping = [
            {
              mobileOrderIndex: 8,
              key: 'typeOfSecurity',
              title: 'Type of Security',
            },
            {
              mobileOrderIndex: 10,
              key: 'currency',
              title: 'Currency',
            },
            {
              mobileOrderIndex: 3,
              key: 'numberOfShares',
              title: 'Number of Shares',
            },
            {
              mobileOrderIndex: 7,
              key: 'sharesHeldDirectly',
              title: (
                <>
                  Shares Held (Directly)
                  <Tooltip dynamicPosition contentStyle={{ left: 0 }} className={cx('tooltip')} tooltipClassName={cx('tooltip-content')}>
                    Number of shares held directly post-transaction.
                  </Tooltip>
                </>
              ),
            },
            {
              mobileOrderIndex: 5,
              key: 'sharesHeldIndirectly',
              title: (
                <>
                  Shares Held (Indirectly)
                  <Tooltip dynamicPosition contentStyle={{ left: 0 }} className={cx('tooltip')} tooltipClassName={cx('tooltip-content')}>
                    Number of shares held through other companies or entities post-transaction.
                  </Tooltip>
                </>
              ),
            },
            {
              mobileOrderIndex: 9,
              key: 'sharesHeldTotal',
              title: (
                <>
                  Shares Held (Total)
                  <Tooltip dynamicPosition contentStyle={{ left: 0 }} className={cx('tooltip')} tooltipClassName={cx('tooltip-content')}>
                    Number of shares held in total post-transaction.
                  </Tooltip>
                </>
              ),
            },
          ];

          if (width <= 1024 || (width <= 1700 && isWatchlistPage)) {
            contentDataMapping.unshift(
              {
                mobileOrderIndex: 2,
                key: 'title',
                title: 'Insider’s Title',
              },
              {
                mobileOrderIndex: 6,
                key: 'filingDateFormatted',
                title: 'Filing Date',
              },
            );
            contentDataMapping.splice(4, 0, {
              mobileOrderIndex: 11,
              key: 'transactionInfoType',
              title: (
                <>
                  Type
                  <Tooltip
                    className={cx('tooltip')}
                    tooltipClassName={cx('tooltip-content')}
                    dynamicPosition
                    contentStyle={{ left: 0 }}
                  >
                    Uninformative transactions are those that have been scheduled in advanced
                    for a predetermined time and number of shares, based on Rule 10b5-1 of the SEC.
                    They are automatically triggered.
                  </Tooltip>
                </>
              ),
            }, {
              mobileOrderIndex: 1,
              key: 'pricePerShare',
              title: 'Price per Share',
            });
          }
          if (width < 768) {
            contentDataMapping.unshift({
              mobileOrderIndex: 4,
              key: 'date',
              title: 'Transaction Date',
            });
            contentDataMapping.sort((a, b) => a.mobileOrderIndex - b.mobileOrderIndex);

            if (multipleCompanies) {
              contentDataMapping.unshift({
                mobileOrderIndex: 0,
                key: 'name',
                title: 'Insider’s Name',
              });
            } else {
              [contentDataMapping[0], contentDataMapping[1]] = [
                contentDataMapping[1],
                contentDataMapping[0],
              ];
              contentDataMapping.splice(4, 0, contentDataMapping.pop());
            }
          }

          const viewData = transactions.map((transaction) => {
            transaction.transactionType = transaction.transactionType && (
              transaction.transactionType
                .toLowerCase()
                .split(' ')
                .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                .join(' ')
            );
            transaction.numberOfShares = checkAndRoun(transaction.numberOfShares, 2, true);
            transaction.name = {
              // TODO uncomment next lines when slugs would be mapped right
              // url: (
              //   transaction.officerSlug
              //   && `${DIRECTORIES_PATH}/people/${transaction.officerSlug}`
              // ),
              url: undefined,
              value: transaction.officerName,
            };
            transaction.title = transaction.officerTitle;
            transaction.date = moment(transaction.transactionDate).format('DD MMM YYYY');
            transaction.filingDateFormatted = moment(transaction.filingDate).format('DD MMM YYYY');
            transaction.pricePerShare = transaction.pricePerShare
              ? checkAndRoun(transaction.pricePerShare, 2, true)
              : '-';
            transaction.valueFormatted = transaction.value ? checkAndRoun(transaction.value, 2, true) : '-';
            transaction.transactionInfoType = !transaction.isAutomatic ? 'Informative' : 'Uninformative';

            transaction.sharesHeldDirectly = transaction.sharesHeldDirectly
              ? checkAndRoun(transaction.sharesHeldDirectly, 2, true)
              : '-';
            transaction.sharesHeldIndirectly = transaction.sharesHeldIndirectly ? checkAndRoun(
              transaction.sharesHeldIndirectly,
              2,
              true,
            ) : '-';
            transaction.sharesHeldTotal = transaction.sharesHeldTotal
              ? checkAndRoun(transaction.sharesHeldTotal, 2, true)
              : '-';

            if (multipleCompanies) {
              let { companyName } = transaction;

              if (companiesData?.length) {
                companyName = companiesData.find((data) => (
                  data.tickers?.includes(transaction.ticker)
                ))?.name || transaction.companyName;
              }

              transaction.companyName = companyName;
            }
            transaction.companyName = {
              url: transaction.companyURL ? convertPermalinkToPath(`${transaction.companyURL}/${INSIDER_TRANSACTIONS}`) : undefined,
              value: transaction.companyName,
            };

            return {
              uid: transaction.uid,
              mainData: {
                name: transaction.name,
                title: transaction.title || '-',
                date: transaction.date,
                filingDateFormatted: transaction.filingDateFormatted,
                transactionType: transaction.transactionType,
                transactionInfoType: transaction.transactionInfoType,
                pricePerShare: transaction.pricePerShare,
                valueFormatted: (
                  <span className="text-nowrap">
                    {transaction.valueFormatted}
                  </span>
                ),
                companyName: transaction.companyName,
                value: transaction.value,
                filingDate: transaction.filingDate,
                transactionDate: transaction.transactionDate,
              },
              collapseContent: (
                <div className={cx('row-content', { watchlist: isWatchlistPage })}>
                  <div className={cx('row-content-data')}>
                    {contentDataMapping.map(({ key, title }) => (
                      <div key={key} className={cx('column')}>
                        <div className={cx('title')}>
                          {title}
                        </div>
                        <div className={cx('value')}>
                          {transaction[key] instanceof Object && ('url' in transaction[key]) ? (
                            <CFLink
                              to={transaction[key].url}
                              external={!!transaction[key].url?.startsWith('http')}
                            >
                              {transaction[key].value}
                            </CFLink>
                          ) : (
                            transaction[key] || '-'
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className={cx('filling-btn-wrapper')}>
                    <TransparentButton
                      type="anchor"
                      to={transaction.filingURL && convertPermalinkToPath(transaction.filingURL)}
                      external={false}
                      className={cx('filling-btn')}
                    >
                      See Original Filing
                    </TransparentButton>
                  </div>
                </div>
              ),
            };
          });

          setTransactionsData((current) => ({
            data: multipleCompanies && currentPage > 1 ? current.data.concat(viewData) : viewData,
            loading: false,
            sortField: null,
            sortOrder: 'asc',
          }));
          if (isWatchlistPage && wlPageLoadingStep !== 'LOADED') {
            dispatch(setWatchlistLoadStatus('LOADED'));
          }

          if (!initialized.current) {
            initialized.current = true;

            if (!multipleCompanies) {
              const savedFilters = await getSavedFilters(identifier);

              const officers = removeDuplicates(
                transactions.map((transaction) => ({
                  label: transaction.officerName,
                  value: transaction.officerName,
                  selected: savedFilters?.officers?.includes(transaction.officerName) ?? true,
                }))
                  .filter((item) => item.value.trim())
                  .sort((a, b) => (
                    a.label < b.label ? -1 : Number(a.label !== b.label)
                  )),
                'value',
              );

              setSelectedOfficers(
                officers
                  .filter((officer) => officer.selected)
                  .map((officer) => officer.name),
              );
              setOfficers(officers);
              setTransactionTypes(
                removeDuplicates(
                  transactions.map((transaction) => ({
                    label: transaction.transactionType,
                    value: transaction.transactionType.toUpperCase(),
                    selected: (
                      savedFilters
                        ?.transactionTypes
                        ?.includes(transaction.transactionType.toUpperCase())
                      ?? true
                    ),
                  }))
                    .filter((item) => item.value.trim())
                    .sort((a, b) => (
                      a.label < b.label ? -1 : Number(a.label !== b.label)
                    )),
                  'value',
                ),
              );

              if (savedFilters?.timeRange && !isEqual(savedFilters?.timeRange, timeRange)) {
                setTimeRange(savedFilters.timeRange);
              }
            }
          }
        }
      });
    }, 200);

    return () => {
      mounted = false;
      clearTimeout(timer);
    };
  }, dataFetchingDeps);

  if (isWatchlistPage && wlPageLoadingStep !== 'LOADED') {
    return null;
  }

  return (
    <section className={Styles['insider-transactions-table']}>
      <div className={Styles.controls}>
        <FilterButton
          icon={<FontAwesomeIcon icon={faRedo} />}
          content="Refresh"
          onClick={updateRefreshFlag}
          mobileIconOnly
        />
        <FilterButton
          icon={<FontAwesomeIcon icon={faFilter} />}
          content="Filters"
          active={filtersOpened}
          onClick={toggleFilters}
          mobileIconOnly
        />
      </div>
      <div hidden={!filtersOpened} className={Styles['filters-wrapper']}>
        <div className={Styles.filters}>
          <CFDropdown
            options={transactionTypes}
            label="Transactions"
            className={Styles.filter}
            onSelect={setSelectedTransactionTypes}
            multipleSelect
            useMobileView
          />
          <CFDropdown
            options={transactionInfoTypes}
            label="Type"
            allSelectedLabel="All Types"
            className={Styles.filter}
            onSelect={setSelectedTransactionInfoTypes}
            multipleSelect
            useMobileView
            tooltip="Uninformative transactions are those that have been scheduled in advanced for a predetermined time and number of shares, based on Rule 10b5-1 of the SEC. They are automatically triggered."
          />
          {!multipleCompanies && (
            <CFDropdown
              options={officers}
              label="Insider's name"
              allSelectedLabel="All Names"
              className={Styles.filter}
              onSelect={setSelectedOfficers}
              multipleSelect
              useMobileView
            />
          )}
          <TimePeriodFilter
            onDateSelect={setTimeRange}
            className={Styles.filter}
            startDate={timeRange.startDate}
            endDate={timeRange.endDate}
            dateFilterType={timeRange.dateFilterType}
            tooltip="Filter by the filing date. Multiple transactions with different dates may be recorded in a single filing."
          />
          <Tooltip
            icon={(
              <CustomButton className={cx('reset', { 'skip-hover': isDefaultSettings })} type="blue" onClick={reset}>
                RESET
              </CustomButton>
            )}
            tooltipClassName={Styles['tooltip-content']}
            disabled={!isDefaultSettings}
          >
            Default Settings - Nothing to Reset.
          </Tooltip>
        </div>
      </div>
      <Preloader
        loading={transactionsData.loading && !transactionsData.data.length}
        centered
      />
      <div
        className={cx('content', {
          loading: !multipleCompanies && transactionsData.loading && transactionsData.data.length,
          'multiple-companies': multipleCompanies,
          'data-exists': transactionsData.data.length,
        })}
      >
        {transactionsData.data.length ? (
          <>
            <DataTableCollapsable
              data={(
                !multipleCompanies ? (
                  transactionsData.data.slice((currentPage - 1) * pageSize, currentPage * pageSize)
                ) : (
                  transactionsData.data
                )
              )}
              columns={insiderTransactionsTableColumns}
              onSort={onSort}
            />
          </>
        ) : (
          !transactionsData.loading && (
            <div className={Styles['no-data']}>
              No matching transactions. Try adjusting your filters.
            </div>
          )
        )}
      </div>
      <div className={Styles.pagination}>
        {Boolean(transactionsData.data.length) && (
          !multipleCompanies ? (
            <Pagination
              currentPage={currentPage}
              totalCount={transactionsData.data.length}
              setCurrentPage={setCurrentPage}
              pageSize={pageSize}
              setSelectedValue={setPageSize}
              className="pagination-bar"
              rightAligned
              skipMobileValues
            />
          ) : (
            <>
              <Preloader
                loading={transactionsData.loading && !!transactionsData.data.length}
                centered
                static
              />
              <div ref={lastElementRef} />
            </>
          )
        )}
      </div>
    </section>
  );
};

InsiderTransactionsTable.defaultProps = {
  officers: [],
  singlePage: false,
  multipleCompanies: false,
  companiesData: [],
};

export default memo(withComponentName(InsiderTransactionsTable));
